// React
import { useState } from 'react';

// Store
import useCoachStore from '../../store/coachStore.js';

// Hooks
import { useAppTheme } from '../../hooks/useAppTheme.js';

//Components
import { DirectionsRunOutlined, EditOutlined, EmailOutlined, PhoneOutlined } from '@mui/icons-material';
import { Avatar, Box, Card, CardContent, Chip, Grid, IconButton, Typography } from '@mui/material';

// Style
import { amber, blue, blueGrey, brown, cyan, deepOrange, deepPurple, green, grey, indigo, lightBlue, lightGreen, lime, orange, pink, purple, red, teal, yellow }
    from '@mui/material/colors/index.js';
import { darkTheme } from '../../styles/colorSchemes/index.js';

// Page
import EditUser from '../dialog/EditUser.js';

const CoachList = (props) => {
    const { athletes, coaches, isEditable, search, showAthleteAmount, onRefreshUserLists } = props;
    const { openEditUser, setOpenEditUser } = useCoachStore();
    const { isDarkMode } = useAppTheme();

    const [selectedCoach, setSelectedCoach] = useState();
    const [selectedColor, setSelectedColor] = useState();
    const [colors] = useState([teal[500], green[500], purple[500], deepPurple[500], lightBlue[500], cyan[500], deepOrange[500], brown[500], blueGrey[500],
    lightGreen[500], lime[500], yellow[500], amber[500], indigo[500], blue[500], orange[500], grey[500], red[500], pink[500]]);

    const convertBufferToBase64String = (coach) => {
        return btoa(String.fromCharCode(...new Uint8Array(coach.profile.picture.data)));
    }

    return (
        <Box sx={{ p: coaches !== undefined && coaches[0] === undefined ? 0 : 2, position: 'relative', minHeight: coaches !== undefined && coaches[0] === undefined ? { xs: '400px', sm: '500px', md: '600px' } : 'auto' }}>
            {selectedCoach !== undefined && (
                <EditUser key={selectedCoach.profile.email} color={selectedColor} open={openEditUser}
                    onClose={() =>
                        setOpenEditUser(false)
                    }
                    profileType={'coach'}
                    person={selectedCoach}
                    athletes={athletes}
                    onSuccess={onRefreshUserLists} />)
            }

            {coaches !== undefined && coaches[0] === undefined && (
                <Box sx={{
                    position: 'absolute',
                    top: { xs: '80px', sm: '100px', md: '120px' },
                    left: 0,
                    right: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: { xs: 2, sm: 4, md: 6 },
                    py: { xs: 4, sm: 5, md: 6 }
                }}>
                    <Box sx={{
                        width: { xs: 60, sm: 70, md: 80 },
                        height: { xs: 60, sm: 70, md: 80 },
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <DirectionsRunOutlined sx={{ fontSize: { xs: 48, sm: 54, md: 60 }, color: isDarkMode ? darkTheme.textSecondary : grey[400] }} />
                    </Box>
                    <Typography variant='h6' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'} fontWeight={600} mb={1} sx={{ fontSize: { xs: '1.1rem', sm: '1.15rem', md: '1.25rem' } }}>
                        Keine Trainer vorhanden
                    </Typography>
                    <Typography variant='body2' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'} sx={{ textAlign: 'center', maxWidth: { xs: 300, sm: 350, md: 400 }, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                        Es sind derzeit keine Trainer zugeordnet.
                    </Typography>
                </Box>)
            }

            <Grid container spacing={2.5}>
                {coaches !== undefined && coaches.filter(coach => coach.profile.lastName !== null)
                    .filter(coach => `${coach.profile.firstName} ${coach.profile.lastName}` !== 'Squash Admin')
                    .sort((a, b) => {
                        return String(a.profile.lastName).localeCompare(String(b.profile.lastName))
                    })
                    .filter(coach => coach.profile.lastName.toLowerCase().includes(String(search).toLowerCase()) ||
                        coach.profile.firstName.toLowerCase().includes(String(search).toLowerCase()))
                    .map((coach, index) => {
                        const cardColor = colors[(index * coaches.length) % colors.length];
                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={index * 10 + 1}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        borderRadius: 4,
                                        boxShadow: isDarkMode ? '0 4px 16px rgba(0, 0, 0, 0.3)' : '0 4px 16px rgba(0, 0, 0, 0.08)',
                                        bgcolor: isDarkMode ? darkTheme.accordionBg : undefined,
                                        transition: 'all 0.3s ease',
                                        position: 'relative',
                                        overflow: 'visible',
                                        '&:hover': {
                                            boxShadow: isDarkMode ? '0 8px 24px rgba(0, 0, 0, 0.4)' : '0 8px 24px rgba(0, 0, 0, 0.15)',
                                            transform: 'translateY(-4px)'
                                        }
                                    }}
                                >
                                    {/* Edit Button */}
                                    {isEditable && (
                                        <IconButton
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                bgcolor: isDarkMode ? darkTheme.headerBg : 'white',
                                                color: isDarkMode ? darkTheme.textSecondary : undefined,
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                zIndex: 1,
                                                '&:hover': {
                                                    bgcolor: cardColor,
                                                    color: 'white',
                                                    transform: 'rotate(90deg)',
                                                    transition: 'transform 0.3s ease'
                                                }
                                            }}
                                            onClick={() => {
                                                setOpenEditUser(true);
                                                setSelectedCoach(coach);
                                                setSelectedColor(cardColor);
                                            }}
                                        >
                                            <EditOutlined fontSize="small" />
                                        </IconButton>
                                    )}

                                    {/* Header with Gradient */}
                                    <Box
                                        sx={{
                                            background: isDarkMode 
                                                ? `linear-gradient(135deg, ${cardColor}33 0%, ${cardColor}66 100%)` 
                                                : `linear-gradient(135deg, ${cardColor}22 0%, ${cardColor}55 100%)`,
                                            borderRadius: '16px 16px 0 0',
                                            p: 3,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 1.5
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                width: 100,
                                                height: 100,
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                                bgcolor: cardColor,
                                                color: isDarkMode ? darkTheme.accordionBg : 'white',
                                                cursor: isEditable ? 'pointer' : 'default',
                                                fontSize: '2.5rem',
                                                transition: 'transform 0.2s ease',
                                                '&:hover': {
                                                    transform: isEditable ? 'scale(1.05)' : 'none'
                                                }
                                            }}
                                            src={`data:image/png;base64,${convertBufferToBase64String(coach)}`}
                                            onClick={
                                                isEditable
                                                    ? () => {
                                                        setOpenEditUser(true);
                                                        setSelectedCoach(coach);
                                                        setSelectedColor(cardColor);
                                                    }
                                                    : undefined
                                            }
                                        >

                                        </Avatar>

                                        <Typography
                                            variant='h6'
                                            fontWeight={600}
                                            align='center'
                                            sx={{
                                                color: isDarkMode ? darkTheme.textPrimary : 'text.primary',
                                                lineHeight: 1.2
                                            }}
                                        >
                                            {coach.profile.firstName} {coach.profile.lastName}
                                        </Typography>

                                        <Chip
                                            label="Trainer"
                                            size="small"
                                            sx={{
                                                bgcolor: isDarkMode ? darkTheme.headerBg : 'white',
                                                color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                fontWeight: 600,
                                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)'
                                            }}
                                        />
                                    </Box>

                                    {/* Content */}
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            {/* Email */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <EmailOutlined sx={{ color: isDarkMode ? darkTheme.textSecondary : 'action.active', fontSize: 18 }} />
                                                <Typography
                                                    variant='body2'
                                                    color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        flex: 1
                                                    }}
                                                >
                                                    {coach.profile.email}
                                                </Typography>
                                            </Box>

                                            {/* Phone */}
                                            {coach.profile.telephone && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <PhoneOutlined sx={{ color: isDarkMode ? darkTheme.textSecondary : 'action.active', fontSize: 18 }} />
                                                    <Typography variant='body2' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}>
                                                        {coach.profile.telephone}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Athlete Count */}
                                            {showAthleteAmount && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        mt: 1,
                                                        p: 1.5,
                                                        borderRadius: 2,
                                                        bgcolor: isDarkMode ? `${cardColor}22` : `${cardColor}11`,
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <DirectionsRunOutlined sx={{ color: cardColor, fontSize: 20 }} />
                                                    <Typography
                                                        variant='body1'
                                                        fontWeight={600}
                                                        sx={{ color: cardColor }}
                                                    >
                                                        {coach.athleteAmount} Athleten
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )
                    })
                }
            </Grid>
        </Box>
    );
}

export default CoachList;